import React, { FC, memo, ReactElement } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Form } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { Formik } from 'formik';
import i18next from 'i18next';
import DatePicker from 'react-datepicker';
import AppModal from '../../../components/app-modal/AppModal';
import { DateTime } from "luxon";

type SaveAndPublishModalProps = {
  show: boolean;
  onPositive: (date: Date) => Promise<void>;
  onNegative: () => void;
  initialValues: {
    date: Date;
  };
};

type SaveAndPushModalFormValues = {
  date: Date | null;
}

const SaveAndPublishModal: FC<SaveAndPublishModalProps> = ({ show, onPositive, onNegative, initialValues }: SaveAndPublishModalProps): ReactElement => {
  const maxDate: DateTime = DateTime.now().plus({ days: 28 });
  
  return (
    <Formik<SaveAndPushModalFormValues>
      enableReinitialize
      initialValues={initialValues}
      validateOnMount
      onSubmit={async ({ date }, { setSubmitting, setFieldValue }) => {
        if(!date) {
          return;
        }
        
        await onPositive(date);

        setSubmitting(false);
        void setFieldValue('date', null);
      }}
      validate={({ date }) => {
        const errors: { date?: string } = {};

        if (!date) {
          errors.date = i18next.t('trainingPlan.validation.date');
        } else {
          const today = DateTime.now().startOf('day');
          const maxSelectableDate = today.plus({ days: 28 });
          const selectedDate = DateTime.fromJSDate(date).startOf('day');

          if (selectedDate.toMillis() > maxSelectableDate.toMillis()) {
            errors.date = i18next.t('trainingPlan.validation.greaterDate');
          } else if (selectedDate.toMillis() < today.toMillis()) {
            errors.date = i18next.t('trainingPlan.validation.pastDate');
          }
        }

        return errors;
      }}
    >
      {({ values, errors, isValid, isSubmitting, handleSubmit, setFieldValue }) => (
        <AppModal show={show} onHide={onNegative}>
          <Form onSubmit={handleSubmit} noValidate>
            <Modal.Header closeButton>
              <Modal.Title>{i18next.t('trainingPlan.publish')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <p>{i18next.t('trainingPlan.saveAndPublishModal.body')}</p>

              <Form.Group controlId="date" bsPrefix="md-form form-group w-50">
                <Form.Label>
                  {i18next.t('trainingPlan.saveAndPublishModal.label')}
                </Form.Label>

                <DatePicker
                  selected={values.date}
                  onChange={(date: Date | null) => {
                    setFieldValue('date', date);
                  }}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="DD-MM-YYYY"
                  className="form-control"
                  minDate={DateTime.now().toJSDate()}
                  maxDate={maxDate.toJSDate()}
                />

                {errors.date && (
                  <div className="invalid-feedback d-block">
                    {errors.date}
                  </div>
                )}
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                style={{ marginRight: 'auto' }}
                onClick={onNegative}
              >
                {i18next.t('generic.cancel')}
              </Button>

              <Button
                type="submit"
                disabled={!isValid || isSubmitting}
                variant="danger"
              >
                {i18next.t('trainingPlan.publish')}
              </Button>
            </Modal.Footer>
          </Form>
        </AppModal>
      )}
    </Formik>
  );
};

export default memo(SaveAndPublishModal);
